import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Bookmark,
  Filter,
  Loader2,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Search,
  Star,
  Terminal,
  Trash2,
  X,
} from 'lucide-react';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import Pagination from '../../../Shared/Pagination/Pagination';
import { PaginationResponse } from '../../../Shared/Pagination/PaginationResponse';
import { useSearchParams } from 'react-router-dom';
import {
  createSavedQuery,
  deleteSavedQuery,
  getDataConnections,
  getSavedQueries,
  getTableColumns,
  runSavedQuery,
  updateSavedQuery,
} from '../../Services/DataService';
import {
  ColumnMeta,
  DataConnection,
  DataQueryResult,
  DataSavedQuery,
  DataSavedQueryRequest,
  DataSavedQueryStats,
  ENVIRONMENT_STYLES,
} from '../../Types/DataServiceType';
import DataResultGrid from './DataResultGrid';
import { primaryKeyLabelsFor } from './recordLink';

const PAGE_SIZE = 8;

const emptyStats: DataSavedQueryStats = {
  total: 0,
  favorites: 0,
  withRawSql: 0,
  onThisConnection: 0,
};

const formatDate = (value?: string | null) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const StatCard = ({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
}) => (
  <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
    <div className="flex items-center justify-between">
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${accent}`}>{icon}</span>
      <span className="text-2xl font-black text-slate-100">{value}</span>
    </div>
    <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
  </div>
);

const SavedQueriesComponent = () => {
  const [searchParams] = useSearchParams();

  const [queries, setQueries] = useState<DataSavedQuery[]>([]);
  const [pagination, setPagination] = useState<PaginationResponse<DataSavedQuery> | null>(null);
  const [stats, setStats] = useState<DataSavedQueryStats>(emptyStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [connections, setConnections] = useState<DataConnection[]>([]);
  const [connectionFilter, setConnectionFilter] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DataSavedQuery | null>(null);
  const [form, setForm] = useState<DataSavedQueryRequest | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<DataSavedQuery | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [runningId, setRunningId] = useState('');
  const [result, setResult] = useState<DataQueryResult | null>(null);
  const [resultError, setResultError] = useState('');
  const [ranQuery, setRanQuery] = useState<DataSavedQuery | null>(null);
  const [resultMeta, setResultMeta] = useState<ColumnMeta[]>([]);

  const draft = useMemo(() => {
    const raw = searchParams.get('draft');
    if (!raw) return null;
    try {
      return JSON.parse(raw) as Partial<DataSavedQueryRequest>;
    } catch {
      return null;
    }
  }, [searchParams]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadConnections = useCallback(async () => {
    try {
      const response = await getDataConnections({}, 0, 100);
      if (response.success && response.data) {
        setConnections(response.data.page?.content ?? []);
      }
    } catch (err) {
      console.error('Failed to load connections', err);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  // Hand off from the query builder or the SQL console, if we arrived with a draft.
  useEffect(() => {
    if (!draft) return;
    setForm({
      name: '',
      description: '',
      connectionId: draft.connectionId ?? '',
      tableName: draft.tableName ?? '',
      filtersJson: draft.filtersJson,
      // A console hand-off carries a statement rather than builder state.
      rawSql: draft.rawSql,
      sortJson: draft.sortJson,
      limitValue: draft.limitValue ?? 100,
      isFavorite: false,
    });
    setEditing(null);
    setFormError('');
    setModalOpen(true);
    // Only fire once for a given draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const fetchQueries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getSavedQueries(
        {
          search: debouncedSearch || undefined,
          connectionId: connectionFilter || undefined,
          favoritesOnly: favoritesOnly || undefined,
        },
        page,
        PAGE_SIZE
      );

      if (response.success && response.data) {
        const pageData = response.data.page;
        setQueries(pageData.content ?? []);
        setStats(response.data.stats ?? emptyStats);
        setPagination(pageData);
        if (pageData.totalPages > 0 && page >= pageData.totalPages) {
          setPage(pageData.totalPages - 1);
        }
      } else {
        setQueries([]);
        setStats(emptyStats);
        setPagination(null);
        setError(response.message || 'Could not load saved queries.');
      }
    } catch (err) {
      console.error('Failed to load saved queries', err);
      setQueries([]);
      setStats(emptyStats);
      setPagination(null);
      setError('An unexpected error occurred while loading saved queries.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, connectionFilter, favoritesOnly, page]);

  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, connectionFilter, favoritesOnly]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '',
      description: '',
      connectionId: connectionFilter || connections[0]?.id || '',
      tableName: '',
      limitValue: 100,
      isFavorite: false,
    });
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (query: DataSavedQuery) => {
    setEditing(query);
    setForm({
      name: query.name,
      description: query.description ?? '',
      connectionId: query.connectionId,
      tableName: query.tableName ?? '',
      filtersJson: query.filtersJson ?? undefined,
      sortJson: query.sortJson ?? undefined,
      limitValue: query.limitValue ?? 100,
      isFavorite: Boolean(query.isFavorite),
    });
    setFormError('');
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form) return;
    if (!form.name.trim()) {
      setFormError('A name is required.');
      return;
    }
    if (!form.connectionId) {
      setFormError('Pick the connection this query runs against.');
      return;
    }
    if (!form.rawSql && !form.tableName) {
      setFormError('A saved query needs either a table or a raw statement.');
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      const payload: DataSavedQueryRequest = {
        ...form,
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        tableName: form.tableName?.trim() || undefined,
        filtersJson: form.filtersJson || undefined,
        sortJson: form.sortJson || undefined,
      };

      const response = editing
        ? await updateSavedQuery(editing.id, payload)
        : await createSavedQuery(payload);

      if (response.success) {
        setAlert({
          variant: 'success',
          title: 'Saved',
          message: editing ? 'Saved query updated.' : 'Saved query created.',
          autoCloseMs: 4000,
          onClose: () => setAlert(null),
        });
        setModalOpen(false);
        fetchQueries();
      } else {
        setFormError(response.message || 'The saved query could not be written.');
      }
    } catch (err) {
      console.error('Failed to save query', err);
      setFormError('The saved query could not be written.');
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async (query: DataSavedQuery) => {
    setRunningId(query.id);
    setResultError('');
    setRanQuery(query);
    try {
      const response = await runSavedQuery(query.id);
      if (response.success && response.data) {
        setResult(response.data);
      } else {
        setResult(null);
        setResultError(response.message || 'The saved query could not be run.');
      }
    } finally {
      setRunningId('');
    }
  };

  /**
   * A saved query keeps the table it was built against, so its result can be
   * drilled into the same way a fresh builder run is. Raw-statement saved
   * queries have no table and stay inert.
   */
  useEffect(() => {
    let cancelled = false;

    if (!ranQuery?.connectionId || !ranQuery.tableName) {
      setResultMeta([]);
      return;
    }

    (async () => {
      const response = await getTableColumns(ranQuery.connectionId, ranQuery.tableName as string);
      if (cancelled) return;
      setResultMeta(response?.success && response.data ? response.data : []);
    })();

    return () => {
      cancelled = true;
    };
  }, [ranQuery]);

  const recordLink = useMemo(
    () => ({
      connectionId: ranQuery?.connectionId ?? '',
      table: ranQuery?.tableName ?? '',
      primaryKeyLabels: primaryKeyLabelsFor(resultMeta, result?.columns ?? []),
    }),
    [ranQuery, resultMeta, result]
  );

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await deleteSavedQuery(deleteTarget.id);
      if (response.success) {
        setAlert({
          variant: 'success',
          title: 'Deleted',
          message: `"${deleteTarget.name}" was removed.`,
          autoCloseMs: 4000,
          onClose: () => setAlert(null),
        });
        setDeleteTarget(null);
        fetchQueries();
      } else {
        setAlert({
          variant: 'error',
          title: 'Delete failed',
          message: response.message || 'The saved query could not be removed.',
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } finally {
      setDeleting(false);
    }
  };

  const toggleFavorite = async (query: DataSavedQuery) => {
    const response = await updateSavedQuery(query.id, {
      name: query.name,
      description: query.description,
      connectionId: query.connectionId,
      tableName: query.tableName,
      filtersJson: query.filtersJson,
      // The update replaces the whole record, so rawSql has to be echoed back
      // or favouriting a raw statement would silently erase it.
      rawSql: query.rawSql,
      sortJson: query.sortJson,
      limitValue: query.limitValue,
      isFavorite: !query.isFavorite,
    });
    if (response.success) {
      fetchQueries();
    } else {
      setAlert({
        variant: 'error',
        title: 'Update failed',
        message: response.message || 'The favourite flag could not be changed.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    }
  };

  const hasActiveFilters = Boolean(search) || favoritesOnly || Boolean(connectionFilter);

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Bookmark size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Saved Queries</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Your reusable reads, kept private to your account
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchQueries}
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={openCreate}
            className="flex items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400"
          >
            <Plus size={15} /> New
          </button>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Total"
          value={stats.total}
          icon={<Bookmark size={16} />}
          accent="border-cyan-500/30 bg-cyan-500/10 text-cyan-400"
        />
        <StatCard
          label="Favourites"
          value={stats.favorites}
          icon={<Star size={16} />}
          accent="border-amber-500/30 bg-amber-500/10 text-amber-400"
        />
        <StatCard
          label="With raw SQL"
          value={stats.withRawSql}
          icon={<Terminal size={16} />}
          accent="border-violet-500/30 bg-violet-500/10 text-violet-400"
        />
        <StatCard
          label="On filter"
          value={stats.onThisConnection}
          icon={<Filter size={16} />}
          accent="border-sky-500/30 bg-sky-500/10 text-sky-400"
        />
      </div>

      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="relative md:col-span-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or description..."
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-9 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <select
            value={connectionFilter}
            onChange={(e) => setConnectionFilter(e.target.value)}
            className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
          >
            <option value="" className="bg-slate-900 text-slate-100">
              All connections
            </option>
            {connections.map((item) => (
              <option key={item.id} value={item.id} className="bg-slate-900 text-slate-100">
                {item.alias}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            onClick={() => setFavoritesOnly(!favoritesOnly)}
            className={`rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition ${
              favoritesOnly
                ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
                : 'border-slate-800 bg-slate-950 text-slate-400 hover:text-slate-200'
            }`}
          >
            <Star size={11} className="mr-1 inline" /> Favourites only
          </button>

          {hasActiveFilters && (
            <button
              onClick={() => {
                setSearch('');
                setFavoritesOnly(false);
                setConnectionFilter('');
              }}
              className="ml-auto flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-rose-400 hover:border-rose-500/40"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[0, 1].map((idx) => (
            <div key={idx} className="animate-pulse rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
              <div className="mb-4 h-5 w-48 rounded bg-slate-800" />
              <div className="h-4 w-full rounded bg-slate-800/70" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 py-16 text-center text-sm font-bold text-rose-400">
          {error}
        </div>
      ) : queries.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
          <Bookmark size={26} className="mx-auto mb-2 text-slate-600" />
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">No saved queries</p>
          <button onClick={openCreate} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
            Save your first query
          </button>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="space-y-3">
            {queries.map((query) => (
              <div
                key={query.id}
                className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 transition hover:border-slate-700"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-black tracking-tight text-slate-100">{query.name}</h3>
                      {query.isFavorite && (
                        <Star size={13} className="fill-amber-400 text-amber-400" />
                      )}
                      {query.hasRawSql && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-violet-500/30 bg-violet-500/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-violet-400">
                          <Terminal size={10} /> Raw SQL
                        </span>
                      )}
                      {query.connectionEnvironment && (
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                            ENVIRONMENT_STYLES[query.connectionEnvironment] ??
                            'border-slate-700 bg-slate-950 text-slate-400'
                          }`}
                        >
                          {query.connectionEnvironment}
                        </span>
                      )}
                    </div>

                    {query.description && (
                      <p className="mt-1.5 line-clamp-2 text-xs text-slate-500">{query.description}</p>
                    )}

                    <p className="mt-2 text-[11px] font-bold text-slate-500">
                      {query.connectionAlias || 'Unknown connection'}
                      {query.tableName ? ` · ${query.tableName}` : ''} · limit {query.limitValue ?? 100} ·
                      run {query.runCount ?? 0} times · last run {formatDate(query.lastRunAt)}
                    </p>

                    {query.missingColumns && query.missingColumns.length > 0 && (
                      <p className="mt-2 flex items-start gap-1.5 text-[11px] font-bold text-amber-400">
                        <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                        No longer a column on this table: {query.missingColumns.join(', ')}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => toggleFavorite(query)}
                      className="rounded-lg border border-slate-800 bg-slate-950 p-2 text-slate-400 transition hover:border-amber-500/40 hover:text-amber-400"
                      title={query.isFavorite ? 'Remove favourite' : 'Mark as favourite'}
                    >
                      <Star size={12} className={query.isFavorite ? 'fill-current' : ''} />
                    </button>
                    <button
                      onClick={() => handleRun(query)}
                      disabled={runningId === query.id}
                      className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
                    >
                      {runningId === query.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Play size={12} />
                      )}
                      Run
                    </button>
                    <button
                      onClick={() => openEdit(query)}
                      className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-slate-300 transition hover:border-cyan-500/40"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={() => setDeleteTarget(query)}
                      className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-rose-400 transition hover:border-rose-500/40"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {pagination && pagination.totalElements > 0 && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
              <Pagination
                page={pagination.page}
                size={pagination.size}
                totalElements={pagination.totalElements}
                totalPages={pagination.totalPages}
                first={pagination.first}
                last={pagination.last}
                hasNext={pagination.hasNext}
                hasPrevious={pagination.hasPrevious}
                setPage={setPage}
              />
            </div>
          )}
        </motion.div>
      )}

      {(result || resultError) && (
        <div className="mt-6">
          <h2 className="mb-3 text-sm font-black tracking-tight text-slate-100">Last run</h2>
          <DataResultGrid
          result={result}
          error={resultError}
          recordLink={recordLink}
          emptyMessage="The saved query returned no rows."
          />
        </div>
      )}

      {/* Create / edit */}
      <AnimatePresence>
        {modalOpen && form && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => !saving && setModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              onClick={(e) => e.stopPropagation()}
              className="my-8 w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                    <Bookmark size={18} />
                  </span>
                  <div>
                    <h2 className="text-base font-black tracking-tight text-slate-100">
                      {editing ? 'Edit saved query' : 'New saved query'}
                    </h2>
                    <p className="text-[11px] font-semibold text-slate-500">Visible only to you</p>
                  </div>
                </div>
                <button
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-4 px-6 py-5">
                {formError && (
                  <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs font-bold text-rose-300">
                    {formError}
                  </div>
                )}

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Weekly active employers"
                    className="w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Description
                  </label>
                  <textarea
                    rows={2}
                    value={form.description ?? ''}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="What this query is for..."
                    className="w-full resize-none rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                      Connection *
                    </label>
                    <select
                      value={form.connectionId}
                      onChange={(e) => setForm({ ...form, connectionId: e.target.value })}
                      className="w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
                    >
                      <option value="" className="bg-slate-900 text-slate-100">
                        Select a connection
                      </option>
                      {connections.map((item) => (
                        <option key={item.id} value={item.id} className="bg-slate-900 text-slate-100">
                          {item.alias} ({item.environment})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                      Table
                    </label>
                    <input
                      type="text"
                      value={form.tableName ?? ''}
                      onChange={(e) => setForm({ ...form, tableName: e.target.value })}
                      placeholder="From the query builder"
                      className="w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                      Row limit
                    </label>
                    <input
                      type="number"
                      value={form.limitValue ?? 100}
                      onChange={(e) => setForm({ ...form, limitValue: Number(e.target.value) })}
                      className="w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 outline-none transition focus:border-cyan-500/50"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, isFavorite: !form.isFavorite })}
                      className={`flex w-full items-center gap-2 rounded-xl border px-3.5 py-2.5 text-xs font-bold transition ${
                        form.isFavorite
                          ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
                          : 'border-slate-800 bg-slate-950 text-slate-400'
                      }`}
                    >
                      <Star size={14} className={form.isFavorite ? 'fill-current' : ''} />
                      {form.isFavorite ? 'Favourite' : 'Mark as favourite'}
                    </button>
                  </div>
                </div>

                <p className="rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-[11px] font-semibold text-slate-500">
                  Filters and sort travel with the query and are revalidated against the live schema
                  each time it runs. If a column has since been dropped, the run fails with the
                  missing names rather than silently returning different data.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-slate-800 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
                >
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Bookmark size={15} />}
                  {saving ? 'Saving' : 'Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
                <AlertTriangle size={20} />
              </span>
              <div>
                <h3 className="text-base font-black tracking-tight text-slate-100">Delete saved query?</h3>
                <p className="mt-1 text-sm text-slate-400">
                  <span className="font-bold text-slate-200">{deleteTarget.name}</span> will be removed
                  permanently.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-rose-400 disabled:opacity-50"
              >
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {deleting ? 'Deleting' : 'Delete'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SavedQueriesComponent;
